<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useTheme } from "@/composables/useTheme";

import {
	AiMagicIcon,
	BookOpen01Icon,
	Money03Icon,
	FolderOpenIcon,
	Home03Icon,
	PrinterIcon,
	Layers01Icon,
	Building01Icon,
	UserIcon,
	Logout01Icon,
	Moon02Icon,
	Sun03Icon,
	SidebarLeftIcon,
	ArrowLeft01Icon,
	Crown02Icon,
	SentIcon,
	PaintBrushIcon,
	GlobeIcon,
	CreditCardIcon,
	Tag01Icon,
	InspectCodeIcon,
	CloudUploadIcon,
	DiscordIcon,
	HourglassIcon,
	Invoice01Icon,
	HardDriveIcon,
	ArrowReloadHorizontalIcon,
	LaptopIcon,
	DatabaseRestoreIcon,
	Analytics01Icon,
	BubbleChatIcon,
	SlidersHorizontalIcon,
	Settings01Icon,
	Archive02Icon,
	UserMultipleIcon,
	Calendar03Icon,
} from "@/components/icons";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const { isDark, toggleTheme, initFromProfile } = useTheme();

const sidebarOpen = ref(false);

onMounted(() => {
	initFromProfile();
});

// Grouped as PrintHQ Cloud's admin: every existing screen keeps its route
// and appears once. Marketplace moderation isn't listed: there's no
// marketplace to moderate. Announcements are part of the Discord Bot screen.
const groups = [
	{ title: "Overview", items: [{ name: "Admin Home", path: "/admin", icon: Home03Icon }] },
	{ title: "Users & Billing", items: [
		{ name: "Users", path: "/admin/users", icon: UserIcon },
		{ name: "Subscriptions", path: "/admin/subscriptions", icon: CreditCardIcon },
		{ name: "Free Trials", path: "/admin/trials", icon: HourglassIcon },
		{ name: "Payments", path: "/admin/payments", icon: Invoice01Icon },
		{ name: "Affiliates", path: "/admin/affiliates", icon: UserMultipleIcon },
		{ name: "Gift Codes", path: "/admin/gift-codes", icon: Tag01Icon },
	] },
	{ title: "Cloud Services", items: [
		{ name: "Storage", path: "/admin/storage", icon: HardDriveIcon },
		{ name: "Sync Activity", path: "/admin/sync", icon: ArrowReloadHorizontalIcon },
		{ name: "Devices", path: "/admin/devices", icon: LaptopIcon },
		{ name: "Backups", path: "/admin/backups", icon: DatabaseRestoreIcon },
		{ name: "Usage & Limits", path: "/admin/usage", icon: Analytics01Icon },
		{ name: "Planner", path: "/admin/planner", icon: Calendar03Icon },
	] },
	{ title: "Global Catalogs", items: [
		{ name: "Global Catalogs", path: "/admin/global-catalogs", icon: FolderOpenIcon },
		{ name: "Material Presets", path: "/admin/material-presets", icon: PaintBrushIcon },
		{ name: "Multicolor Units", path: "/admin/multicolor-units", icon: Layers01Icon },
		{ name: "Printer Manufacturers", path: "/admin/printer-manufacturers", icon: GlobeIcon },
		{ name: "Printer Models", path: "/admin/printer-models", icon: PrinterIcon },
		{ name: "Printer Templates", path: "/admin/printer-templates", icon: PrinterIcon },
		{ name: "Filament Brands", path: "/admin/filament-brands", icon: Building01Icon },
		{ name: "Filament Products", path: "/admin/filament-products", icon: Layers01Icon },
		{ name: "Filament Submissions", path: "/admin/filament-submissions", icon: SentIcon },
		{ name: "Printer Submissions", path: "/admin/printer-submissions", icon: SentIcon },
	] },
	{ title: "Kairo AI", items: [
		{ name: "Kairo AI", path: "/admin/kairo", icon: AiMagicIcon },
		{ name: "Kairo Knowledge", path: "/admin/kairo/knowledge", icon: BookOpen01Icon },
		{ name: "Kairo Credits", path: "/admin/kairo/credits", icon: Money03Icon },
	] },
	{ title: "Community", items: [
		{ name: "Discord Bot", path: "/admin/integrations/discord", icon: DiscordIcon },
		{ name: "User Feedback", path: "/admin/feedback", icon: BubbleChatIcon },
	] },
	{ title: "System", items: [
		{ name: "Feature Flags", path: "/admin/flags", icon: SlidersHorizontalIcon },
		{ name: "Settings", path: "/admin/settings", icon: Settings01Icon },
		{ name: "Audit Logs", path: "/admin/audit", icon: Archive02Icon },
		{ name: "G-code Analyzer", path: "/admin/gcode-analyzer", icon: InspectCodeIcon },
		{ name: "Bridge Releases", path: "/admin/bridge-releases", icon: CloudUploadIcon },
		{ name: "Playground", path: "/admin/playground", icon: InspectCodeIcon },
	] },
];

const isActive = (path: string) => {
	if (path === "/admin") {
		return route.path === "/admin";
	}
	return route.path.startsWith(path);
};

const handleSignOut = async () => {
	await authStore.signOut();
	router.push("/login");
};

const closeSidebar = () => {
	sidebarOpen.value = false;
};

const initial = () => authStore.displayName?.charAt(0).toUpperCase() || "?";
</script>

<template>
	<div class="min-h-screen bg-bg text-fg">
		<!-- Drawer scrim -->
		<div v-if="sidebarOpen" class="fixed inset-0 z-30 bg-black/40 backdrop-blur-[3px] lg:hidden" aria-hidden="true" @click="closeSidebar" />

		<!-- Sidebar -->
		<aside
			class="app-sidebar fixed inset-y-0 left-0 z-40 w-[248px] flex flex-col bg-sidebar border-r border-line transition-transform duration-150 lg:translate-x-0"
			:class="sidebarOpen ? 'translate-x-0 shadow-float' : '-translate-x-full'"
		>
			<!-- Logo -->
			<div class="h-12 shrink-0 flex items-center gap-2 px-4">
				<img src="/logo-wordmark.svg" alt="PrintHQ" class="w-[104px] h-auto" />
				<span class="chip ml-auto">
					<Crown02Icon :size="11" class="text-warn" />
					Admin
				</span>
			</div>

			<!-- Navigation -->
			<nav class="flex-1 min-h-0 overflow-y-auto px-3 py-1" aria-label="Admin navigation">
				<RouterLink to="/" class="nav-item mb-3" @click="closeSidebar">
					<ArrowLeft01Icon :size="17" />
					<span>Back to PrintHQ</span>
				</RouterLink>

				<section v-for="g in groups" :key="g.title" class="mb-3" :aria-label="g.title">
					<div class="section-title h-6 flex items-center px-2">{{ g.title }}</div>
					<ul class="flex flex-col gap-px">
						<li v-for="item in g.items" :key="item.path">
							<RouterLink
								:to="item.path"
								class="nav-item"
								:aria-current="isActive(item.path) ? 'page' : undefined"
								@click="closeSidebar"
							>
								<component :is="item.icon" :size="17" />
								<span class="flex-1 min-w-0 truncate">{{ item.name }}</span>
							</RouterLink>
						</li>
					</ul>
				</section>
			</nav>

			<!-- Account -->
			<div class="border-t border-line p-2 flex items-center gap-2.5">
				<img v-if="authStore.avatarUrl" :src="authStore.avatarUrl" alt="" class="size-6 rounded-full object-cover shrink-0 ml-1.5" />
				<span v-else class="size-6 ml-1.5 rounded-full bg-accent-soft text-accent-text text-[11px] font-semibold flex items-center justify-center shrink-0">{{ initial() }}</span>
				<span class="flex-1 min-w-0 truncate text-[13px] font-medium">{{ authStore.displayName }}</span>
				<button class="icon-btn-sm" aria-label="Sign out" title="Sign out" @click="handleSignOut">
					<Logout01Icon :size="15" />
				</button>
			</div>
		</aside>

		<!-- View -->
		<div class="app-main lg:ml-[248px]">
			<header class="app-toolbar sticky top-0 z-20 h-12 flex items-center gap-2 px-4 bg-bg border-b border-line">
				<button class="icon-btn -ml-1.5 lg:hidden" aria-label="Open navigation" title="Open navigation" @click="sidebarOpen = true">
					<SidebarLeftIcon :size="17" />
				</button>
				<h1 class="text-[15px] leading-[21.75px] font-semibold tracking-normal truncate">
					{{ route.meta.title || "Admin" }}
				</h1>
				<span class="flex-1" />
				<button
					class="icon-btn"
					:aria-label="isDark ? 'Switch to light theme' : 'Switch to dark theme'"
					:title="isDark ? 'Light theme' : 'Dark theme'"
					@click="toggleTheme"
				>
					<Sun03Icon v-if="isDark" />
					<Moon02Icon v-else />
				</button>
			</header>

			<main class="p-4 sm:p-6">
				<slot />
			</main>
		</div>
	</div>
</template>
